import React, { useState, useEffect, useCallback } from 'react';
import { api } from '../lib/api';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { toast } from 'sonner';
import { Repeat, Plus, Loader2, Check, X, Pause, Play, PenTool, Mail, Linkedin, Phone, AlertTriangle, ChevronDown, SkipForward, Send, Users, Pencil, CalendarDays, ShieldCheck } from 'lucide-react';
import { Textarea } from './ui/textarea';
import { CcPicker } from './CcPicker';
import { CollapsibleSection } from './CollapsibleSection';
import { FollowupOutcomeDialog } from './FollowupOutcomeDialog';

const CHANNEL_ICONS = { letter: PenTool, email: Mail, linkedin: Linkedin, call: Phone };
const CHANNEL_LABELS = { letter: 'Letter', email: 'Email', linkedin: 'LinkedIn', call: 'Call' };
const STATUS_CHIP = {
  active: 'text-emerald-300 border-emerald-600/40 bg-emerald-600/10',
  paused: 'text-amber-300 border-amber-600/40 bg-amber-600/10',
  completed: 'text-sky-300 border-sky-600/40 bg-sky-600/10',
  cancelled: 'text-muted-foreground border-border bg-secondary/40',
};
const FIELD_LABELS = { private_address: 'Private address', email: 'Email', linkedin_url: 'LinkedIn URL', phone_work: 'Phone' };

function EnrollDialog({ open, onOpenChange, projectId, onEnrolled }) {
  const [templates, setTemplates] = useState([]);
  const [templateId, setTemplateId] = useState('');
  const [dealInfo, setDealInfo] = useState(null);
  const [contacts, setContacts] = useState([]);
  const [search, setSearch] = useState('');
  const [contact, setContact] = useState(null);
  const [readiness, setReadiness] = useState(null);
  const [quick, setQuick] = useState({});
  const [cc, setCc] = useState([]);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) { setContact(null); setReadiness(null); setSearch(''); setQuick({}); setCc([]); return; }
    api.getSequenceTemplates(projectId).then(r => {
      // Legacy default is only offered for deals approved before the prio cut-off; new deals get their prio sequence preselected.
      const info = r.data.deal || null;
      const list = r.data.templates.filter(t => !t.is_default || info?.legacy);
      setTemplates(list); setDealInfo(info);
      const rec = list.find(t => t.id === info?.recommended_template_id) || list.find(t => t.prio) || list[0];
      setTemplateId(rec?.id || '');
      setCc(r.data.settings?.default_cc || []);
    }).catch(() => {});
    api.getProjectContacts(projectId).then(r => setContacts(r.data.contacts || [])).catch(() => {});
  }, [open, projectId]);

  useEffect(() => {
    if (!open || !search.trim()) return;
    const t = setTimeout(() => {
      api.getContacts(search.trim(), '').then(r => setContacts(r.data.contacts || [])).catch(() => {});
    }, 300);
    return () => clearTimeout(t);
  }, [search, open]);

  const checkReadiness = useCallback((c, tid) => {
    if (!c) return;
    api.getSequenceReadiness(c.id, tid)
      .then(r => { setReadiness(r.data); setQuick({}); })
      .catch(() => setReadiness(null));
  }, []);

  useEffect(() => { if (contact) checkReadiness(contact, templateId); }, [contact, templateId, checkReadiness]);

  const saveQuick = async () => {
    const patch = Object.fromEntries(Object.entries(quick).filter(([, v]) => (v || '').trim()));
    if (!Object.keys(patch).length) return;
    setBusy(true);
    try {
      await api.updateContact(contact.id, patch);
      toast.success('Contact updated');
      checkReadiness(contact, templateId);
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
    setBusy(false);
  };

  const enroll = async () => {
    setBusy(true);
    try {
      await api.enrollInSequence({ contact_id: contact.id, project_id: projectId, template_id: templateId, cc_emails: cc });
      toast.success('Sequence started');
      onOpenChange(false);
      onEnrolled();
    } catch (err) { toast.error(err.response?.data?.detail || 'Enroll failed'); }
    setBusy(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-card border-border rounded-sm max-w-lg max-h-[90vh] overflow-y-auto" data-testid="enroll-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading text-lg text-foreground">Start Outreach Sequence</DialogTitle>
        </DialogHeader>
        <div className="space-y-3 mt-1">
          <div>
            <p className="text-[10px] text-foreground/40 uppercase tracking-wider mb-1">Sequence</p>
            <select value={templateId} onChange={e => setTemplateId(e.target.value)} data-testid="enroll-template-select"
              className="w-full text-sm bg-secondary border border-border text-foreground rounded-sm px-2 h-9 cursor-pointer focus:outline-none">
              {templates.map(t => <option key={t.id} value={t.id}>{t.name}{t.is_default ? ' (Legacy)' : ''}</option>)}
            </select>
            {dealInfo && (
              <p className="text-[10px] text-muted-foreground mt-1" data-testid="enroll-deal-hint">
                {dealInfo.legacy ? 'Legacy-Deal (vor der Prio-Umstellung freigegeben) — läuft weiter in der Default-Sequenz.'
                  : dealInfo.prio ? `Deal hat Prio ${dealInfo.prio} — empfohlene Sequenz vorausgewählt.` : (dealInfo.note || '')}
              </p>
            )}
          </div>
          <div>
            <p className="text-[10px] text-foreground/40 uppercase tracking-wider mb-1">Contact</p>
            {contact ? (
              <div className="flex items-center gap-2 p-2 bg-secondary border border-border rounded-sm">
                <span className="text-xs text-foreground truncate">{[contact.first_name, contact.last_name].filter(Boolean).join(' ') || contact.company}</span>
                {contact.company && <span className="text-[10px] text-muted-foreground truncate">· {contact.company}</span>}
                <button onClick={() => { setContact(null); setReadiness(null); }} className="ml-auto text-muted-foreground hover:text-red-400"><X className="w-3.5 h-3.5" /></button>
              </div>
            ) : (
              <>
                <Input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search contacts…"
                  data-testid="enroll-contact-search"
                  className="bg-secondary border-border text-foreground rounded-sm h-8 text-xs mb-1.5" />
                <div className="space-y-0.5 max-h-40 overflow-y-auto border border-border rounded-sm p-1">
                  {contacts.length === 0 ? (
                    <p className="text-[11px] text-muted-foreground p-1.5">No contacts — link contacts to this deal or search all.</p>
                  ) : contacts.map((c, i) => (
                    <button key={c.id} onClick={() => setContact(c)} data-testid={`enroll-contact-${i}`}
                      className="w-full flex items-center gap-2 px-1.5 py-1 rounded-sm hover:bg-secondary/60 text-left">
                      <span className="text-xs text-foreground truncate">{[c.first_name, c.last_name].filter(Boolean).join(' ') || c.company}</span>
                      {c.company && <span className="text-[10px] text-muted-foreground truncate">· {c.company}</span>}
                      {c.type && <span className="ml-auto text-[9px] font-mono text-violet-300 border border-violet-600/40 bg-violet-600/10 rounded px-1 py-0.5">{c.type}</span>}
                    </button>
                  ))}
                </div>
              </>
            )}
          </div>

          {contact && readiness && (
            <div className={`border rounded-sm p-3 space-y-2 ${readiness.ready && !readiness.missing?.length ? 'border-emerald-600/40 bg-emerald-600/5' : 'border-amber-600/40 bg-amber-600/5'}`}
              data-testid="readiness-checklist">
              {readiness.ready && !readiness.missing?.length ? (
                <p className="flex items-center gap-1.5 text-xs text-emerald-400"><Check className="w-3.5 h-3.5" /> All required contact data present — ready to start.</p>
              ) : (
                <>
                  <p className="flex items-center gap-1.5 text-xs text-amber-400" data-testid="readiness-missing">
                    <AlertTriangle className="w-3.5 h-3.5 shrink-0" /> Missing: {readiness.missing.map(m => m.label).join(', ')}
                    {readiness.ready ? ' — LinkedIn is optional, the sequence adapts:' : ' — private address, email & phone are mandatory, add them below.'}
                  </p>
                  {readiness.ready && readiness.changes?.length > 0 && (
                    <ul className="text-[10px] text-amber-300/80 space-y-0.5 pl-5 list-disc" data-testid="readiness-changes">
                      {readiness.changes.map((c, i) => <li key={i}>{c}</li>)}
                    </ul>
                  )}
                  <div className="space-y-1.5">
                    {readiness.missing.map(m => (
                      <div key={m.field} className="flex items-center gap-2">
                        <span className="text-[10px] text-muted-foreground w-24 shrink-0">{FIELD_LABELS[m.field] || m.label}</span>
                        <Input value={quick[m.field] || ''} onChange={e => setQuick(q => ({ ...q, [m.field]: e.target.value }))}
                          placeholder={m.field === 'linkedin_url' ? 'https://linkedin.com/in/…' : ''}
                          data-testid={`quick-edit-${m.field}`}
                          className="bg-secondary border-border text-foreground rounded-sm h-7 text-xs" />
                      </div>
                    ))}
                    <Button size="sm" onClick={saveQuick} disabled={busy} data-testid="quick-edit-save-btn"
                      className="bg-amber-700 hover:bg-amber-600 text-white rounded-sm h-7 text-[11px] px-2.5">
                      {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : 'Save to contact'}
                    </Button>
                  </div>
                </>
              )}
            </div>
          )}

          <div>
            <p className="text-[10px] text-foreground/40 uppercase tracking-wider mb-1 flex items-center gap-1">
              <Users className="w-3 h-3 text-emerald-400" /> CC colleagues (sequence emails)
            </p>
            <CcPicker value={cc} onChange={setCc} />
          </div>

          <div className="flex justify-end gap-2 pt-2 border-t border-border">
            <Button variant="ghost" onClick={() => onOpenChange(false)} className="text-muted-foreground rounded-sm text-xs h-8 px-3">Cancel</Button>
            <Button onClick={enroll} disabled={busy || !contact || !readiness?.ready} data-testid="enroll-submit-btn"
              className="bg-violet-700 hover:bg-violet-600 text-white rounded-sm text-xs h-8 px-4">
              {busy && <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />} Start Sequence
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export function StepRow({ step, idx, isCurrent, active, onDone, onSkip, enrollmentId, onChanged, approveEmails }) {
  const [openContent, setOpenContent] = useState(false);
  const [editing, setEditing] = useState(false);
  const [approving, setApproving] = useState(false);
  const [subj, setSubj] = useState('');
  const [bodyText, setBodyText] = useState('');
  const [saving, setSaving] = useState(false);
  const [outcomeOpen, setOutcomeOpen] = useState(false);
  const [outcomeBusy, setOutcomeBusy] = useState(false);
  const [reschedOpen, setReschedOpen] = useState(false);
  const [reschedDate, setReschedDate] = useState('');
  const [resching, setResching] = useState(false);
  const Icon = CHANNEL_ICONS[step.channel] || Mail;
  const color = step.status === 'done' ? 'text-emerald-400' : step.status === 'skipped' ? 'text-muted-foreground' : isCurrent ? 'text-amber-400' : 'text-muted-foreground/50';
  // Max (20.10.2026): sequences with "Mails vor Versand freigeben" hold every e-mail until it is released here
  const gated = (step.approve_required != null ? !!step.approve_required : !!approveEmails) && step.channel === 'email' && step.status === 'pending';
  const needsApproval = gated && !step.approved;
  const todayIso = new Date().toISOString().slice(0, 10);
  const dueNow = !step.scheduled_date || step.scheduled_date <= todayIso;
  const fmtD = (d) => (d ? new Date(`${d}T00:00:00`).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit' }) : '');

  const approve = async (sendNow) => {
    setSaving(true);
    try {
      const r = await api.approveSequenceStep(enrollmentId, idx, { subject: subj, body: bodyText, send_now: sendNow });
      toast.success(r.data.sent ? 'Mail freigegeben und gesendet — Sequenz läuft weiter' : `Freigegeben — Versand automatisch am ${fmtD(r.data.scheduled_date)}`);
      setEditing(false); setApproving(false);
      onChanged && onChanged();
    } catch (err) {
      const d = err.response?.data?.detail;
      toast.error((typeof d === 'string' && d) || 'Freigabe fehlgeschlagen');
    }
    setSaving(false);
  };

  const reschedule = async () => {
    if (!reschedDate) return;
    setResching(true);
    try {
      const r = await api.rescheduleSequenceStep(enrollmentId, idx, reschedDate);
      toast.success(r.data.weekend_shifted
        ? `Verschoben auf ${r.data.scheduled_date} (Wochenende übersprungen)`
        : `Verschoben auf ${r.data.scheduled_date}`);
      setReschedOpen(false);
      onChanged && onChanged();
    } catch (err) {
      const d = err.response?.data?.detail;
      toast.error((typeof d === 'string' && d) || 'Verschieben fehlgeschlagen');
    }
    setResching(false);
  };

  const completeCall = async (payload) => {
    setOutcomeBusy(true);
    try {
      await api.completeSequenceStep(enrollmentId, idx, payload);
      toast.success(payload.followup_outcome
        ? 'Call-Ergebnis erfasst — zählt als Anrufversuch in den Outreach Statistics'
        : 'Step completed');
      setOutcomeOpen(false);
      onChanged && onChanged();
    } catch (err) {
      const d = err.response?.data?.detail;
      toast.error((d && (d.message || (typeof d === 'string' && d))) || 'Failed');
    }
    setOutcomeBusy(false);
  };

  const markDone = () => {
    if (step.channel === 'call' && enrollmentId) { setOutcomeOpen(true); return; }
    onDone();
  };

  const startEdit = () => {
    setSubj(step.rendered_subject || '');
    setBodyText(step.rendered || '');
    setEditing(true);
  };
  const saveEdit = async () => {
    setSaving(true);
    try {
      await api.updateEnrollmentStepContent(enrollmentId, idx, { subject: subj, body: bodyText });
      toast.success('Content updated — this exact text will go out');
      setEditing(false);
      onChanged && onChanged();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to save'); }
    setSaving(false);
  };

  return (
    <div className={`flex gap-2.5 ${isCurrent ? '' : 'opacity-80'}`} data-testid={`enrollment-step-${idx}`}>
      <div className="flex flex-col items-center">
        <span className={`w-6 h-6 rounded-full border flex items-center justify-center shrink-0 ${step.status === 'done' ? 'border-emerald-500/60 bg-emerald-500/10' : isCurrent ? 'border-amber-500/60 bg-amber-500/10' : 'border-border bg-secondary/40'}`}>
          {step.status === 'done' ? <Check className="w-3 h-3 text-emerald-400" /> : <Icon className={`w-3 h-3 ${color}`} />}
        </span>
        <span className="w-px flex-1 bg-border" />
      </div>
      <div className="pb-3 min-w-0 flex-1">
        <div className="flex items-center gap-2 flex-wrap">
          <span className={`text-xs font-medium ${step.status === 'skipped' ? 'line-through text-muted-foreground' : 'text-foreground'}`}>
            {CHANNEL_LABELS[step.channel]}
          </span>
          {step.fallback_from && (
            <span className="text-[9px] font-mono text-amber-300 border border-amber-600/40 bg-amber-600/10 rounded px-1 py-0.5"
              title={`No data for ${CHANNEL_LABELS[step.fallback_from]} — replaced automatically`}
              data-testid={`step-fallback-${idx}`}>
              instead of {CHANNEL_LABELS[step.fallback_from]}
            </span>
          )}
          {step.auto_sent && <span className="flex items-center gap-0.5 text-[9px] font-mono text-sky-300 border border-sky-600/40 bg-sky-600/10 rounded px-1 py-0.5"><Send className="w-2.5 h-2.5" /> auto-sent</span>}
          {needsApproval && (
            <span className="flex items-center gap-0.5 text-[9px] font-mono text-amber-300 border border-amber-500/50 bg-amber-500/10 rounded px-1 py-0.5"
              title="Diese Sequenz verlangt eine Freigabe — die Mail wartet, bis sie hier freigegeben wurde" data-testid={`step-needs-approval-${idx}`}>
              <ShieldCheck className="w-2.5 h-2.5" /> Freigabe nötig
            </span>
          )}
          {gated && step.approved && (
            <span className="flex items-center gap-0.5 text-[9px] font-mono text-emerald-300 border border-emerald-500/40 bg-emerald-500/10 rounded px-1 py-0.5"
              title={`Freigegeben von ${step.approved_by || ''}`} data-testid={`step-approved-${idx}`}>
              <ShieldCheck className="w-2.5 h-2.5" /> freigegeben{step.approved_by ? ` · ${step.approved_by.split(' ')[0]}` : ''}
            </span>
          )}
          {step.bounced && (
            <span className="text-[9px] font-mono text-orange-300 border border-orange-500/40 bg-orange-500/10 rounded px-1 py-0.5"
              title="This email could not be delivered — the sequence escalated to the next touch"
              data-testid={`step-bounced-${idx}`}>
              bounced
            </span>
          )}
          <span className="text-[10px] text-muted-foreground font-mono">
            {step.status === 'done' ? `done ${step.actual_date}` : step.status === 'skipped' ? `skipped ${step.actual_date}${step.skip_reason ? ` · ${step.skip_reason}` : ''}` : step.scheduled_date ? `scheduled ${step.scheduled_date}` : `+${step.delay_days}d after previous`}
          </span>
          {isCurrent && step.status === 'pending' && enrollmentId && (
            <button onClick={() => { setReschedDate(step.scheduled_date || ''); setReschedOpen(o => !o); }}
              title="Auf anderen Tag verschieben (z.B. Urlaub des Inhabers)"
              data-testid={`step-reschedule-btn-${idx}`}
              className="text-[10px] text-sky-400 hover:text-sky-300 flex items-center gap-0.5">
              <CalendarDays className="w-3 h-3" /> verschieben
            </button>
          )}
          {(step.rendered || step.rendered_subject) && (
            <button onClick={() => setOpenContent(!openContent)} className="text-[10px] text-muted-foreground hover:text-foreground flex items-center gap-0.5">
              content <ChevronDown className={`w-3 h-3 transition-transform ${openContent ? 'rotate-180' : ''}`} />
            </button>
          )}
        </div>
        {reschedOpen && (
          <div className="mt-1.5 flex items-center gap-1.5 flex-wrap" data-testid={`step-reschedule-row-${idx}`}>
            <input type="date" value={reschedDate} min={new Date().toISOString().slice(0, 10)}
              onChange={ev => setReschedDate(ev.target.value)}
              data-testid={`step-reschedule-date-${idx}`}
              className="bg-secondary border border-border rounded-sm h-7 px-2 text-xs text-foreground" />
            <Button size="sm" onClick={reschedule} disabled={resching || !reschedDate}
              data-testid={`step-reschedule-save-${idx}`}
              className="bg-sky-700 hover:bg-sky-600 text-white rounded-sm h-7 text-[10px] px-2">
              {resching ? <Loader2 className="w-3 h-3 animate-spin" /> : 'Verschieben'}
            </Button>
            <button onClick={() => setReschedOpen(false)}
              className="text-[10px] text-muted-foreground hover:text-foreground">Abbrechen</button>
            <span className="text-[9px] text-muted-foreground/60">Wochenenden werden automatisch übersprungen</span>
          </div>
        )}
        {openContent && (
          <div className="mt-1 text-[11px] text-foreground/70 bg-secondary/30 border border-border rounded-sm p-2 whitespace-pre-wrap">
            {editing ? (
              <div className="space-y-1.5">
                {step.channel === 'email' && (
                  <Input value={subj} onChange={e => setSubj(e.target.value)} placeholder="Subject"
                    data-testid={`step-edit-subject-${idx}`}
                    className="bg-secondary border-border text-foreground rounded-sm h-7 text-xs" />
                )}
                <Textarea value={bodyText} onChange={e => setBodyText(e.target.value)} rows={7}
                  data-testid={`step-edit-body-${idx}`}
                  className="bg-secondary border-border text-foreground rounded-sm text-xs" />
                <div className="flex items-center gap-1.5 flex-wrap">
                  {approving ? (
                    <>
                      <Button size="sm" onClick={() => approve(true)} disabled={saving} data-testid={`step-approve-send-${idx}`}
                        className="bg-emerald-600 hover:bg-emerald-500 text-white rounded-sm h-6 text-[10px] px-2">
                        {saving ? <Loader2 className="w-3 h-3 animate-spin" /> : <><Send className="w-3 h-3 mr-0.5" /> Freigeben & jetzt senden</>}
                      </Button>
                      {!dueNow && (
                        <Button size="sm" variant="outline" onClick={() => approve(false)} disabled={saving} data-testid={`step-approve-later-${idx}`}
                          className="border-emerald-500/40 text-emerald-300 hover:bg-emerald-500/10 rounded-sm h-6 text-[10px] px-2">
                          <ShieldCheck className="w-3 h-3 mr-0.5" /> Freigeben — Versand am {fmtD(step.scheduled_date)}
                        </Button>
                      )}
                    </>
                  ) : (
                    <Button size="sm" onClick={saveEdit} disabled={saving} data-testid={`step-edit-save-${idx}`}
                      className="bg-emerald-600 hover:bg-emerald-500 text-white rounded-sm h-6 text-[10px] px-2">
                      {saving ? <Loader2 className="w-3 h-3 animate-spin" /> : <><Check className="w-3 h-3 mr-0.5" /> Save</>}
                    </Button>
                  )}
                  <Button variant="ghost" size="sm" onClick={() => { setEditing(false); setApproving(false); }}
                    className="text-muted-foreground rounded-sm h-6 text-[10px] px-2">Cancel</Button>
                  {step.channel === 'email' && (
                    <span className="text-[9px] text-muted-foreground/60">signature is appended automatically on send</span>
                  )}
                </div>
              </div>
            ) : (
              <>
                {step.rendered_subject && <p className="font-medium mb-1">Subject: {step.rendered_subject}</p>}
                {step.rendered}
                {step.edited_by && (
                  <p className="text-[9px] text-sky-400 mt-1" data-testid={`step-edited-by-${idx}`}>edited by {step.edited_by}</p>
                )}
                {step.status === 'pending' && enrollmentId && (
                  <div className="mt-1.5">
                    <Button variant="outline" size="sm" onClick={startEdit} data-testid={`step-edit-btn-${idx}`}
                      className="border-border text-muted-foreground hover:text-foreground rounded-sm h-6 text-[10px] px-2">
                      <Pencil className="w-3 h-3 mr-0.5" /> Edit content
                    </Button>
                  </div>
                )}
              </>
            )}
          </div>
        )}
        {isCurrent && active && step.status === 'pending' && (
          <div className="flex gap-1.5 mt-1.5 flex-wrap items-center">
            {needsApproval ? (
              <>
                <Button size="sm" onClick={() => { startEdit(); setApproving(true); setOpenContent(true); }} data-testid={`step-approve-open-${idx}`}
                  className="bg-amber-600 hover:bg-amber-500 text-white rounded-sm h-6 text-[10px] px-2">
                  <ShieldCheck className="w-3 h-3 mr-0.5" /> Prüfen & freigeben
                </Button>
                <span className="text-[10px] text-amber-300/90" data-testid={`step-approval-wait-${idx}`}>
                  {dueNow ? 'Wartet auf Freigabe — wird erst nach Freigabe gesendet' : `Wartet auf Freigabe — Versand geplant ${fmtD(step.scheduled_date)}`}
                </span>
              </>
            ) : step.channel === 'email' && !step.task_id ? (
              <span className="text-[10px] text-sky-400">{gated ? `Freigegeben — sendet automatisch am ${fmtD(step.scheduled_date)}` : `Sends automatically on ${step.scheduled_date}`}</span>
            ) : (
              <Button size="sm" onClick={markDone} data-testid={`step-done-btn-${idx}`}
                className="bg-emerald-600 hover:bg-emerald-500 text-white rounded-sm h-6 text-[10px] px-2">
                <Check className="w-3 h-3 mr-0.5" /> Mark done
              </Button>
            )}
            <Button variant="outline" size="sm" onClick={onSkip} data-testid={`step-skip-btn-${idx}`}
              className="border-border text-muted-foreground hover:text-foreground rounded-sm h-6 text-[10px] px-2">
              <SkipForward className="w-3 h-3 mr-0.5" /> Skip
            </Button>
          </div>
        )}
      </div>
      <FollowupOutcomeDialog
        task={outcomeOpen ? { title: 'Sequenz-Call — Ergebnis erfassen', template_id: `seq:${enrollmentId}:${idx}` } : null}
        busy={outcomeBusy}
        onSelect={(v, extra) => completeCall({
          followup_outcome: v,
          followup_reason_category: extra?.category || null,
          followup_reason_text: extra?.reason || '',
        })}
        onSkip={() => completeCall({ skip_outcome: true })}
        onClose={() => setOutcomeOpen(false)} />
    </div>
  );
}

export function DealSequencesCard({ projectId }) {
  const [enrollments, setEnrollments] = useState([]);
  const [meta, setMeta] = useState({});
  const [enrollOpen, setEnrollOpen] = useState(false);

  const load = useCallback(() => {
    api.getProjectEnrollments(projectId)
      .then(r => {
        setEnrollments(r.data.enrollments || []);
        setMeta({ letter_sent: r.data.letter_sent, letter_sent_at: r.data.letter_sent_at });
      })
      .catch(() => {});
  }, [projectId]);
  useEffect(() => {
    load();
    const h = () => load();
    window.addEventListener('outreach-flow-changed', h);
    return () => window.removeEventListener('outreach-flow-changed', h);
  }, [load]);

  const act = (fn, msg) => async (...args) => {
    try { await fn(...args); if (msg) toast.success(msg); load(); }
    catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  const [confirmBreakup, setConfirmBreakup] = useState(null);

  const visible = enrollments.filter(e => e.status !== 'cancelled');

  return (
    <CollapsibleSection
      storageKey={`deal.${projectId}.sequences`}
      icon={Repeat}
      iconClass="text-violet-400"
      title="Outreach Sequences"
      count={visible.length ? `${visible.length} enrolled` : undefined}
      testId="deal-sequences-card"
      bodyClass="p-4"
      right={
        <Button variant="outline" size="sm" onClick={(e) => { e.stopPropagation(); setEnrollOpen(true); }} data-testid="enroll-open-btn"
          className="border-violet-600/40 text-violet-400 hover:bg-violet-600/10 rounded-sm text-[11px] h-7 px-2.5">
          <Plus className="w-3 h-3 mr-1" /> Enroll contact
        </Button>
      }
    >

      {visible.length === 0 ? (
        meta.letter_sent ? (
          <div className="flex items-center gap-2 flex-wrap border border-amber-600/40 bg-amber-600/5 rounded-sm p-3" data-testid="sequence-missing-banner">
            <AlertTriangle className="w-4 h-4 text-amber-400 shrink-0" />
            <p className="text-xs text-amber-300 flex-1 min-w-[200px]">
              Letter sent{meta.letter_sent_at ? ` on ${meta.letter_sent_at.slice(0, 10)}` : ''} — no follow-up sequence running. The owner won't be chased automatically.
            </p>
            <Button size="sm" onClick={() => setEnrollOpen(true)} data-testid="sequence-missing-start-btn"
              className="bg-amber-700 hover:bg-amber-600 text-white rounded-sm h-7 text-[11px] px-2.5">
              Start sequence now
            </Button>
          </div>
        ) : (
          <p className="text-xs text-muted-foreground italic">
            No sequences running — the follow-up sequence starts automatically once the outreach letter is marked as sent (or enroll a contact manually).
          </p>
        )
      ) : (
        <div className="space-y-3">
          {visible.map((e, ei) => {
            const doneCount = e.steps.filter(s => s.status !== 'pending').length;
            return (
              <div key={e.id} className="border border-border rounded-sm p-3" data-testid={`enrollment-${ei}`}>
                <div className="flex items-center gap-2 flex-wrap mb-2">
                  <span className="text-xs font-medium text-foreground">{e.contact_name}</span>
                  <span className="text-[10px] text-muted-foreground">· {e.template_name}</span>
                  {e.auto_started && (
                    <span className="text-[9px] font-mono text-sky-300 border border-sky-600/40 bg-sky-600/10 rounded px-1 py-0.5" data-testid={`enrollment-auto-${ei}`}>auto-started</span>
                  )}
                  <span className={`text-[9px] font-mono uppercase border rounded px-1.5 py-0.5 ${STATUS_CHIP[e.status] || ''}`} data-testid={`enrollment-status-${ei}`}>{e.status}</span>
                  <span className="text-[10px] font-mono text-muted-foreground">{doneCount}/{e.steps.length}</span>
                  <span className="ml-auto flex gap-1 items-center">
                    {['active', 'paused', 'completed'].includes(e.status) && !e.steps.some(s => s.purpose === 'breakup' && (s.channel === 'letter' || s.status === 'done')) && (
                      confirmBreakup === e.id ? (
                        <span className="flex gap-1 items-center">
                          <span className="text-[9px] text-amber-300">Brief erstellen &amp; freitags versenden?</span>
                          <button onClick={() => { setConfirmBreakup(null); act(() => api.addBreakupStep(e.id), 'Break-up-Schritt ergänzt')(); }}
                            data-testid={`enrollment-add-breakup-confirm-${ei}`}
                            className="text-[9px] font-mono uppercase border border-amber-500 bg-amber-600/20 text-amber-300 hover:bg-amber-600/30 rounded px-1.5 py-0.5">
                            Ja
                          </button>
                          <button onClick={() => setConfirmBreakup(null)} data-testid={`enrollment-add-breakup-cancel-${ei}`}
                            className="text-[9px] font-mono uppercase border border-border text-muted-foreground hover:bg-secondary/50 rounded px-1.5 py-0.5">
                            Nein
                          </button>
                        </span>
                      ) : (
                        <button onClick={() => setConfirmBreakup(e.id)}
                          title="Break-up-Brief ergänzen" data-testid={`enrollment-add-breakup-${ei}`}
                          className="text-[9px] font-mono uppercase border border-amber-600/40 text-amber-400 hover:bg-amber-600/10 rounded px-1.5 py-0.5">
                          + Break-up
                        </button>
                      )
                    )}
                    {e.status === 'active' && (
                      <button onClick={act(() => api.pauseEnrollment(e.id), 'Sequence paused')} title="Pause"
                        data-testid={`enrollment-pause-${ei}`}
                        className="text-muted-foreground hover:text-amber-400 p-0.5"><Pause className="w-3.5 h-3.5" /></button>
                    )}
                    {e.status === 'paused' && (
                      <button onClick={act(() => api.resumeEnrollment(e.id), 'Sequence resumed')} title="Resume"
                        data-testid={`enrollment-resume-${ei}`}
                        className="text-muted-foreground hover:text-emerald-400 p-0.5"><Play className="w-3.5 h-3.5" /></button>
                    )}
                    {['active', 'paused'].includes(e.status) && (
                      <button onClick={() => { if (window.confirm('Cancel this sequence?')) act(() => api.cancelEnrollment(e.id), 'Sequence cancelled')(); }}
                        title="Cancel" data-testid={`enrollment-cancel-${ei}`}
                        className="text-muted-foreground hover:text-red-400 p-0.5"><X className="w-3.5 h-3.5" /></button>
                    )}
                  </span>
                </div>
                {e.status === 'paused' && e.paused_reason && (
                  <p className="flex items-center gap-1.5 text-[11px] text-amber-400 mb-2" data-testid={`enrollment-pause-reason-${ei}`}>
                    <AlertTriangle className="w-3 h-3 shrink-0" /> {e.paused_reason}
                  </p>
                )}
                <div>
                  {e.steps.map((s, i) => (
                    <StepRow key={i} step={s} idx={i} isCurrent={i === e.current_step} active={e.status === 'active'}
                      enrollmentId={e.id} onChanged={load} approveEmails={e.approve_emails}
                      onDone={act(() => api.completeSequenceStep(e.id, i), 'Step completed')}
                      onSkip={act(() => api.skipSequenceStep(e.id, i), 'Step skipped')} />
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      )}

      <EnrollDialog open={enrollOpen} onOpenChange={setEnrollOpen} projectId={projectId} onEnrolled={load} />
    </CollapsibleSection>
  );
}
